import { useCallback, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where, orderBy, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { Article, ArticlePreview } from '../types/article';
import { useAuth } from '../contexts/AuthContext';
import { useUserData } from './useUserData';

export const useArticles = () => {
  const { currentUser } = useAuth();
  const { userData } = useUserData();
  const [loading, setLoading] = useState(false);

  const getArticle = useCallback(async (slug: string): Promise<Article | null> => {
    try {
      const articlesRef = collection(db, 'articles');
      const q = query(articlesRef, where('slug', '==', slug));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) return null;
      
      const articleDoc = querySnapshot.docs[0];
      const article = articleDoc.data() as Article;
      
      // Get next and previous articles in the same language and category
      const allArticlesQuery = query(
        articlesRef,
        where('isPublished', '==', true),
        where('meta.language', '==', article.meta.language),
        where('category', '==', article.category),
        orderBy('year', 'asc'),
        orderBy('order', 'asc')
      );
      const allArticles = await getDocs(allArticlesQuery);
      const articles = allArticles.docs.map(doc => ({
        slug: doc.data().slug,
        title: doc.data().title,
      }));
      
      const currentIndex = articles.findIndex(a => a.slug === slug);
      if (currentIndex > 0) {
        article.previousArticle = articles[currentIndex - 1];
      }
      if (currentIndex < articles.length - 1) {
        article.nextArticle = articles[currentIndex + 1];
      }
      
      return {
        ...article,
        id: articleDoc.id,
      };
    } catch (error) {
      console.error('Error fetching article:', error);
      return null;
    }
  }, []);

  const getArticlePreviews = useCallback(async (language: string = 'en', includeUnpublished: boolean = false): Promise<{ [category: string]: ArticlePreview[] }> => {
    try {
      const articlesRef = collection(db, 'articles');
      const queryConstraints = [
        where('meta.language', '==', language),
        orderBy('year', 'desc'),
        orderBy('order', 'desc')
      ];
      
      if (!includeUnpublished) {
        queryConstraints.unshift(where('isPublished', '==', true));
      }
      
      const q = query(articlesRef, ...queryConstraints);
      const querySnapshot = await getDocs(q);
      
      const articlesByCategory: { [category: string]: ArticlePreview[] } = {};
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const preview: ArticlePreview = {
          id: doc.id,
          slug: data.slug,
          title: data.title,
          lead: data.lead,
          year: data.year,
          order: data.order,
          category: data.category,
          isPublished: data.isPublished,
          meta: {
            title: data.meta.title,
            description: data.meta.description,
            language: data.meta.language,
          },
        };
        
        if (!articlesByCategory[data.category]) {
          articlesByCategory[data.category] = [];
        }
        articlesByCategory[data.category].push(preview);
      });
      
      return articlesByCategory;
    } catch (error) {
      console.error('Error fetching article previews:', error);
      return {};
    }
  }, []);

  const updateArticle = useCallback(async (id: string, updates: Partial<Article>): Promise<boolean> => {
    if (!currentUser || !userData?.isAdmin) return false;
    
    try {
      const articleRef = doc(db, 'articles', id);
      await updateDoc(articleRef, updates);
      return true;
    } catch (error) {
      console.error('Error updating article:', error);
      return false;
    }
  }, [currentUser, userData?.isAdmin]);

  const createArticle = useCallback(async (article: Omit<Article, 'id'>): Promise<string | null> => {
    if (!currentUser || !userData?.isAdmin) return null;
    
    try {
      const articlesRef = collection(db, 'articles');
      const docRef = await addDoc(articlesRef, article);
      return docRef.id;
    } catch (error) {
      console.error('Error creating article:', error);
      return null;
    }
  }, [currentUser, userData?.isAdmin]);

  const deleteArticle = useCallback(async (id: string): Promise<boolean> => {
    if (!currentUser || !userData?.isAdmin) return false;
    
    try {
      const articleRef = doc(db, 'articles', id);
      await deleteDoc(articleRef);
      return true;
    } catch (error) {
      console.error('Error deleting article:', error);
      return false;
    }
  }, [currentUser, userData?.isAdmin]);

  return {
    getArticle,
    getArticlePreviews,
    updateArticle,
    createArticle,
    deleteArticle,
    loading,
  };
}; 